import { usePlanStore } from '@/stores/planStore';
import { useIntegrationsStore } from '@/stores/integrationsStore';
import type { SubscriptionState, UsageSummary } from '@/types';
import { setSnapshot, type AnalyticsProps } from './analytics';

const PLAN_STATUS: Record<SubscriptionState['status'], string> = {
  trialing: 'trial',
  lifetime: 'ltd',
  active: 'monthly',
  canceled: 'expired',
  none: 'expired',
};

export function planSnapshot(
  subscription: SubscriptionState | null,
  usage: UsageSummary | null
): AnalyticsProps {
  if (!subscription) return {};
  return {
    plan_status: subscription.readOnly ? 'expired' : PLAN_STATUS[subscription.status],
    ...(usage?.aiCredits &&
      !usage.aiCredits.unlimited && { ai_credits_used_pct: Math.round(usage.aiCredits.percent) }),
  };
}

export const connectionsSnapshot = (connections: { status: string }[]): AnalyticsProps => ({
  channels_connected_count: connections.filter(c => c.status === 'active').length,
});

// Stamps profile snapshots onto later events as the stores that already hold them change.
export function watchAnalyticsSnapshots(): () => void {
  // Only when a fetch replaced the data: the stores outlive a logout, so an
  // unrelated update (a loading flag) must not re-stamp the previous user's values.
  const stopPlan = usePlanStore.subscribe((s, prev) => {
    if (s.subscription !== prev.subscription || s.usage !== prev.usage) {
      setSnapshot(planSnapshot(s.subscription, s.usage));
    }
  });
  const stopConnections = useIntegrationsStore.subscribe((s, prev) => {
    if (s.connections !== prev.connections) setSnapshot(connectionsSnapshot(s.connections));
  });
  return () => {
    stopPlan();
    stopConnections();
  };
}
